import { HttpService } from './http.service';
import { Config } from '../config';

export type EntityType = 'fleet' | 'authority';

export const CONFIG_TYPES = {
  FLEET_CONFIG: 'fleet.config',
  FLEET_METADATA: 'fleet.metadata',
  FLEET_EVENT_TYPES: 'fleet.event_types',
  FLEET_PERMITTED_PLATES: 'fleet.permitted_plates',
  FLEET_ENFORCEMENT_EXCEPTIONS: 'fleet.enforcement_exceptions',
  FLEET_BLOCK_MAPPING: 'fleet.block_mapping',
  FLEET_OTA_CONFIG: 'fleet.ota_config',
  AUTHORITY_CONFIG: 'authority.config',
  AUTHORITY_METADATA: 'authority.metadata',
} as const;

export type ConfigType = (typeof CONFIG_TYPES)[keyof typeof CONFIG_TYPES];

// Fleet-related config types (excludes authority.config which is a separate entity)
export const FLEET_CONFIG_TYPES = [
  CONFIG_TYPES.FLEET_CONFIG,
  CONFIG_TYPES.FLEET_METADATA,
  CONFIG_TYPES.FLEET_EVENT_TYPES,
  CONFIG_TYPES.FLEET_PERMITTED_PLATES,
  CONFIG_TYPES.FLEET_ENFORCEMENT_EXCEPTIONS,
  CONFIG_TYPES.FLEET_BLOCK_MAPPING,
  CONFIG_TYPES.FLEET_OTA_CONFIG,
] as const;

export const AUTHORITY_CONFIG_TYPES = [CONFIG_TYPES.AUTHORITY_CONFIG, CONFIG_TYPES.AUTHORITY_METADATA] as const;

export const ALL_CONFIG_TYPES = [...FLEET_CONFIG_TYPES, ...AUTHORITY_CONFIG_TYPES] as const;

export const CONFIG_TYPE_LABELS: Record<ConfigType, string> = {
  [CONFIG_TYPES.FLEET_CONFIG]: 'Fleet Config',
  [CONFIG_TYPES.FLEET_METADATA]: 'Fleet Metadata',
  [CONFIG_TYPES.FLEET_EVENT_TYPES]: 'Fleet Event Types',
  [CONFIG_TYPES.FLEET_PERMITTED_PLATES]: 'Permitted Plates',
  [CONFIG_TYPES.FLEET_ENFORCEMENT_EXCEPTIONS]: 'Enforcement Exceptions',
  [CONFIG_TYPES.FLEET_BLOCK_MAPPING]: 'Block Mapping',
  [CONFIG_TYPES.FLEET_OTA_CONFIG]: 'OTA Config',
  [CONFIG_TYPES.AUTHORITY_CONFIG]: 'Authority Config',
  [CONFIG_TYPES.AUTHORITY_METADATA]: 'Authority Metadata',
} as const;

export const formatConfigType = (type: ConfigType): string => CONFIG_TYPE_LABELS[type] ?? type;

export type OperationType = 'create' | 'update' | 'delete';

export enum ChangeRequestStatus {
  Pending = 'pending',
  Applied = 'applied',
  Rejected = 'rejected',
}

export interface CreateChangeRequestPayload {
  entity_id: string;
  config_type: ConfigType;
  target_object_id?: string | null;
  previous_value?: unknown;
  proposed_change?: unknown;
  change_reason: string;
}

export interface ChangeRequest {
  id: number;
  entity_id: string;
  config_type: ConfigType;
  operation: OperationType;
  target_object_id?: string | null;
  previous_value?: unknown;
  proposed_change?: unknown;
  change_reason: string;
  rejection_reason?: string | null;
  change_ticket?: string | null;
  jira_url?: string | null;
  status: ChangeRequestStatus;
  requested_by: string;
  reviewed_by?: string | null;
  created_at: string;
  reviewed_at?: string | null;
}

export interface ChangeRequestsResponse {
  change_requests: ChangeRequest[];
}

export interface SelfReviewPermissionResponse {
  allowed: boolean;
}

export interface ApproveRejectPayload {
  status: 'approved' | 'rejected';
  rejection_reason?: string | null;
}

export abstract class ConfigurationManagementService {
  private static readonly baseUrl = `${Config.API}/config-management-api/v1/config-change-requests`;

  /**
   * Create a new configuration change request
   */
  public static createChangeRequest = (payload: CreateChangeRequestPayload): Promise<ChangeRequest> => {
    return HttpService.fetchAuthorized(ConfigurationManagementService.baseUrl, {
      method: 'POST',
      body: JSON.stringify({ ...payload, operation: 'update' }),
    });
  };

  /**
   * List change requests for a specific entity.
   * @param status - Optional status filter
   */
  public static listChangeRequests = (entityType: EntityType, entityId: string, status?: ChangeRequestStatus): Promise<ChangeRequest[]> => {
    const params = new URLSearchParams({
      entity_type: entityType,
      entity_id: entityId,
      ...(status && { status }),
    });

    return HttpService.fetchAuthorized(`${ConfigurationManagementService.baseUrl}?${params.toString()}`).then(
      (response: ChangeRequest[]) => response,
    );
  };

  /**
   * Get a single change request by ID
   */
  public static getById = (id: number): Promise<ChangeRequest> => {
    return HttpService.fetchAuthorized(`${ConfigurationManagementService.baseUrl}/${id}`);
  };

  /**
   * Check if self-review is allowed for a change request.
   * Returns response with allowed boolean.
   */
  public static getSelfReviewPermission = (id: number): Promise<SelfReviewPermissionResponse> => {
    return HttpService.fetchAuthorized(`${ConfigurationManagementService.baseUrl}/${id}/self-review-permission`);
  };

  /**
   * Approve or reject a change request
   */
  public static approveOrReject = (id: number, payload: ApproveRejectPayload): Promise<void> => {
    return HttpService.fetchAuthorized(`${ConfigurationManagementService.baseUrl}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  };

  /**
   * Approve a change request
   */
  public static approve = (id: number): Promise<void> => {
    return ConfigurationManagementService.approveOrReject(id, {
      status: 'approved',
    });
  };

  /**
   * Reject a change request
   */
  public static reject = (id: number, rejectionReason?: string): Promise<void> => {
    return ConfigurationManagementService.approveOrReject(id, {
      status: 'rejected',
      rejection_reason: rejectionReason,
    });
  };
}
